import React from 'react';
import { createRoot } from 'react-dom/client';
import { ErrorLogger } from '@byscott-io/issuesbyscott_client';
import { configure as configureMetrics, track as metricsTrack } from '@byscott-io/metricsbyscott-web';
import { storage, configureAuth } from '@byscott-io/corebyscott';
import App, { configureBillingSurface } from './App';
import AffiliateApp, { configureAffiliateSurface } from './affiliate/App';

// Host-partitioned surface (#614). Configure corebyscott for the surface that
// will actually mount, BEFORE anything reads the shared config (the metrics /
// storage init below, and every component at render). Both App modules used to
// call configureAuth at import against corebyscott's single shared config
// object, so whichever imported last (affiliate) won — leaking affiliate
// branding, nav, buttons and login onto the billing admin surface. The script
// is `type="module"` (deferred), so the app-surface meta is already in the DOM
// here.
const appSurface = document.querySelector('meta[name="app-surface"]')?.content;
if (appSurface === 'affiliates') {
  configureAffiliateSurface();
} else {
  configureBillingSurface();
}

// Set up client-side error reporting
if (window.ENV?.ISSUESBYSCOTT_API_URL && window.ENV?.ISSUESBYSCOTT_API_KEY) {
  ErrorLogger.configure({
    apiUrl: window.ENV.ISSUESBYSCOTT_API_URL,
    apiKey: window.ENV.ISSUESBYSCOTT_API_KEY,
    environment: window.ENV.RAILS_ENV || 'production',
    enabled: window.ENV.RAILS_ENV === 'production',
    commitSha: window.ENV?.COMMIT_SHA,
    onError: (eventName, properties) => metricsTrack(eventName, properties),
  });
  ErrorLogger.setupGlobalHandler();

  // setupGlobalHandler catches window.onerror, which React render errors do NOT
  // reach -- React swallows them and unmounts the tree. Core's ErrorBoundary is
  // what sees those, and it reports through core's own config, so point it here.
  // It also carries the component stack, which window.onerror cannot know.
  configureAuth({
    onError: (error, info) =>
      ErrorLogger.report(error, { componentStack: info?.componentStack }),
  });
}

// Set up metrics tracking
if (window.ENV?.METRICSBYSCOTT_API_URL && window.ENV?.METRICSBYSCOTT_API_KEY) {
  configureMetrics({
    apiUrl: window.ENV.METRICSBYSCOTT_API_URL,
    apiKey: window.ENV.METRICSBYSCOTT_API_KEY,
    appName: 'billingbyscott',
    autoClickTrackingAnonymous: true,
    enabled: window.ENV.RAILS_ENV === 'production',
    userId: storage.getCurrentUserId() || undefined,
    containerId: storage.getCurrentContainerId() || undefined,
  });
}

document.addEventListener('DOMContentLoaded', () => {
  const container = document.getElementById('root');
  if (container) {
    const root = createRoot(container);
    // Mount the tree matching the surface configured above.
    root.render(appSurface === 'affiliates' ? <AffiliateApp /> : <App />);
  }
});
